import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { dhaftharuApi, type DhaftharuEntry, type Condition, type ColumnSpec } from '../features/dhaftharu/api.ts';
import { ConditionBuilder, isConditionComplete } from '../components/ConditionBuilder.tsx';
import { ColumnPicker } from '../components/ColumnPicker.tsx';
import { RegisterPreviewTable } from '../components/RegisterPreviewTable.tsx';
import { dv } from '../i18n/dv.ts';

export function DhaftharuDetail(): JSX.Element {
  const { id } = useParams({ from: '/dhaftharu/$id' });
  const qc = useQueryClient();
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['dhaftharu', id] });

  // ── Main data ───────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dhaftharu', id],
    queryFn: () => dhaftharuApi.get(id),
  });

  // ── Meta (for ConditionBuilder + ColumnPicker) ──────────────────────────────
  const { data: meta, isLoading: isMetaLoading } = useQuery({
    queryKey: ['dhaftharu-meta'],
    queryFn: dhaftharuApi.meta,
  });

  // ── Local state seeded from server on first load ────────────────────────────
  const seeded = useRef(false);
  const [condConditions, setCondConditions] = useState<Condition[]>([]);
  const [condColumns,    setCondColumns]    = useState<ColumnSpec[]>([]);
  const [manualColumns,  setManualColumns]  = useState<ColumnSpec[]>([]);
  // Conditions + columns live behind a collapsible "Register settings" panel so
  // the page leads with the register itself. Auto-open only when nothing is
  // configured yet (a fresh conditional register needs setting up).
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!data || seeded.current) return;
    seeded.current = true;
    if (data.kind === 'CONDITIONAL') {
      setCondConditions(data.conditions);
      setCondColumns(data.columns ?? []);
      setSettingsOpen(data.conditions.length === 0);
    } else {
      setManualColumns(data.columns ?? []);
    }
  }, [data]);

  // ── Manual-entry mutations ──────────────────────────────────────────────────
  const [search, setSearch]       = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data: residents, isFetching: searching } = useQuery({
    queryKey: ['residents', debounced],
    queryFn: () => dhaftharuApi.searchResidents(debounced),
    enabled: debounced.length > 0,
  });

  const [actionError, setActionError] = useState<string | null>(null);
  const onActionError = (): void => setActionError(dv.common.error);

  const addEntry = useMutation({
    mutationFn: (personId: string) => dhaftharuApi.addEntry(id, personId),
    onSuccess: () => { setActionError(null); void invalidate(); },
    onError: onActionError,
  });
  const removeEntry = useMutation({
    mutationFn: (personId: string) => dhaftharuApi.removeEntry(id, personId),
    onSuccess: () => { setActionError(null); void invalidate(); },
    onError: onActionError,
  });
  const reorder = useMutation({
    mutationFn: (orderedEntryIds: string[]) => dhaftharuApi.reorder(id, orderedEntryIds),
    onSuccess: () => { setActionError(null); void invalidate(); },
    onError: onActionError,
  });

  // ── Generate / download ─────────────────────────────────────────────────────
  const [genMessage, setGenMessage] = useState<string | null>(null);
  const generate = useMutation({
    mutationFn: () => dhaftharuApi.generate(id),
    onSuccess: () => { setGenMessage(dv.dhaftharu.generateQueued); },
    onError:   () => setGenMessage(dv.common.error),
  });
  const downloadPdf = useMutation({ mutationFn: () => dhaftharuApi.downloadPdf(id) });

  // ── CONDITIONAL save ────────────────────────────────────────────────────────
  const [condSaveError, setCondSaveError] = useState<string | null>(null);
  const saveConditional = useMutation({
    mutationFn: () => dhaftharuApi.update(id, { conditions: condConditions, columns: condColumns }),
    onSuccess: () => {
      setCondSaveError(null);
      void invalidate();
    },
    onError: () => setCondSaveError(dv.common.error),
  });

  // ── MANUAL columns save ─────────────────────────────────────────────────────
  const [manualColError, setManualColError] = useState<string | null>(null);
  const saveManualColumns = useMutation({
    mutationFn: () => dhaftharuApi.update(id, { columns: manualColumns }),
    onSuccess: (updated) => {
      setManualColError(null);
      setManualColumns(updated.columns ?? []);
      void invalidate();
    },
    onError: () => setManualColError(dv.common.error),
  });

  // ── Loading / error guards ──────────────────────────────────────────────────
  if (isLoading) {
    return <div className="page"><p className="status-label text-muted-foreground" role="status">{dv.common.loading}</p></div>;
  }
  if (isError || !data) {
    return (
      <div className="page space-y-4">
        <Link to="/dhaftharu" className="btn-ghost"><Icon name="arrow_forward" size={15} /> {dv.dhaftharu.title}</Link>
        <p className="card-paper p-6 text-center text-sm text-muted-foreground">{dv.common.error}</p>
      </div>
    );
  }

  // ── Derived values (manual-only, safe for both kinds) ───────────────────────
  const hasIncompleteConditions = condConditions.some((c) => !isConditionComplete(c));
  const entries: DhaftharuEntry[] = data.kind === 'MANUAL' ? data.entries : [];
  const entryIds            = entries.map((e) => e.id);
  const existingPersonIds   = new Set(entries.map((e) => e.personId));

  function moveEntry(index: number, dir: -1 | 1): void {
    const next = [...entryIds];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    reorder.mutate(next);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="page space-y-6">
      <Link to="/dhaftharu" className="btn-ghost"><Icon name="arrow_forward" size={15} /> {dv.dhaftharu.title}</Link>

      {/* Title + PDF actions */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl tracking-tight">{data.title}</h1>
          {data.description && <p className="text-sm text-muted-foreground mt-1">{data.description}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <button onClick={() => { setGenMessage(null); generate.mutate(); }} disabled={generate.isPending} className="btn-secondary">
              <Icon name="description" size={15} /> {generate.isPending ? dv.dhaftharu.generating : dv.dhaftharu.generate}
            </button>
            <button onClick={() => downloadPdf.mutate()} disabled={downloadPdf.isPending} className="btn-primary">
              <Icon name="download" size={15} /> {downloadPdf.isPending ? dv.dhaftharu.generating : dv.dhaftharu.download}
            </button>
          </div>
          {genMessage && <p className="text-sm text-muted-foreground" role="status">{genMessage}</p>}
          {downloadPdf.isError && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
        </div>
      </header>

      {data.kind === 'MANUAL' ? (
        <>
          {/* ── MANUAL: resident search ── */}
          <section className="card-paper space-y-3 p-4">
            <div className="relative">
              <Icon name="search" size={16} className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                className="input pe-9"
                value={search}
                placeholder={dv.dhaftharu.searchResidents}
                aria-label={dv.dhaftharu.searchResidents}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {debounced.length > 0 && (
              searching ? (
                <p className="status-label text-muted-foreground" role="status">{dv.common.loading}</p>
              ) : (residents ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{dv.dhaftharu.noResults}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {(residents ?? []).map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="font-medium leading-snug truncate">{r.fullName}</p>
                        {r.nationalId && <p className="mono-meta text-muted-foreground" dir="ltr">{r.nationalId}</p>}
                      </div>
                      <button
                        onClick={() => { setActionError(null); addEntry.mutate(r.id); }}
                        disabled={addEntry.isPending || existingPersonIds.has(r.id)}
                        className="btn-secondary shrink-0"
                      >
                        <Icon name="add" size={14} /> {dv.dhaftharu.addEntry}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            )}
          </section>

          {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}

          {/* ── MANUAL: entries list ── */}
          <section className="space-y-3">
            <h2 className="section-heading">
              <span className="font-serif text-lg font-bold">{dv.dhaftharu.entries}</span>
              {entries.length > 0 && <span className="pill pill-neutral shrink-0 tabular">{entries.length}</span>}
            </h2>
            {entries.length === 0 ? (
              <p className="card-paper p-6 text-center text-sm text-muted-foreground">{dv.dhaftharu.noEntries}</p>
            ) : (
              <ol className="card-paper divide-y divide-border overflow-hidden">
                {entries.map((entry: DhaftharuEntry, i) => (
                  <li key={entry.id} className="flex items-center gap-3 p-3">
                    <span className="mono-meta tabular w-6 shrink-0 text-muted-foreground">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug truncate">{entry.person.fullName}</p>
                      {entry.person.nationalId && (
                        <p className="mono-meta text-muted-foreground" dir="ltr">{entry.person.nationalId}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => moveEntry(i, -1)}
                        disabled={i === 0 || reorder.isPending}
                        aria-label={dv.dhaftharu.moveUp}
                        className="btn-ghost px-2 py-1 disabled:opacity-30"
                      >
                        <Icon name="expand_less" size={15} />
                      </button>
                      <button
                        onClick={() => moveEntry(i, 1)}
                        disabled={i === entries.length - 1 || reorder.isPending}
                        aria-label={dv.dhaftharu.moveDown}
                        className="btn-ghost px-2 py-1 disabled:opacity-30"
                      >
                        <Icon name="expand_more" size={15} />
                      </button>
                      <button
                        onClick={() => { setActionError(null); removeEntry.mutate(entry.personId); }}
                        disabled={removeEntry.isPending}
                        aria-label={dv.dhaftharu.removeEntry}
                        className="btn-ghost px-2 py-1 text-destructive hover:text-destructive"
                      >
                        <Icon name="delete" size={15} />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {/* ── MANUAL: register settings (columns) ── */}
          <section className="card-paper overflow-hidden">
            <button
              type="button"
              onClick={() => setSettingsOpen((o) => !o)}
              aria-expanded={settingsOpen}
              className="flex w-full items-center justify-between gap-3 p-4 text-start"
            >
              <span className="flex items-center gap-2">
                <Icon name="settings" size={16} className="shrink-0 text-muted-foreground" />
                <span className="font-serif text-lg font-bold">{dv.dhaftharu.settingsHeading}</span>
              </span>
              <span className="flex items-center gap-2">
                {!settingsOpen && (
                  <span className="mono-meta hidden text-muted-foreground sm:inline">
                    {dv.dhaftharu.settingsColumnsCount(manualColumns.length)}
                  </span>
                )}
                {settingsOpen ? <Icon name="expand_less" size={16} /> : <Icon name="expand_more" size={16} />}
              </span>
            </button>

            {settingsOpen && (
              <div className="space-y-3 border-t border-border p-4">
                <h3 className="font-serif text-base font-bold">{dv.dhaftharu.columnsSection}</h3>
                {isMetaLoading ? (
                  <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
                ) : meta ? (
                  <>
                    <ColumnPicker meta={meta} value={manualColumns} onChange={setManualColumns} />
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => saveManualColumns.mutate()}
                        disabled={saveManualColumns.isPending}
                        className="btn-primary"
                      >
                        {saveManualColumns.isPending ? dv.common.saving : dv.dhaftharu.saveColumns}
                      </button>
                      {manualColError && (
                        <p className="text-sm text-destructive" role="alert">{manualColError}</p>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          {/* ── CONDITIONAL: register settings (conditions + columns) ── */}
          <section className="card-paper overflow-hidden">
            <button
              type="button"
              onClick={() => setSettingsOpen((o) => !o)}
              aria-expanded={settingsOpen}
              className="flex w-full items-center justify-between gap-3 p-4 text-start"
            >
              <span className="flex items-center gap-2">
                <Icon name="settings" size={16} className="shrink-0 text-muted-foreground" />
                <span className="font-serif text-lg font-bold">{dv.dhaftharu.settingsHeading}</span>
              </span>
              <span className="flex items-center gap-2">
                {!settingsOpen && (
                  <span className="mono-meta hidden text-muted-foreground sm:inline">
                    {condConditions.length === 0
                      ? dv.dhaftharu.noConditions
                      : `${dv.dhaftharu.settingsConditionsCount(condConditions.length)} · ${dv.dhaftharu.settingsColumnsCount(condColumns.length)}`}
                  </span>
                )}
                {settingsOpen ? <Icon name="expand_less" size={16} /> : <Icon name="expand_more" size={16} />}
              </span>
            </button>

            {settingsOpen && (
              <div className="space-y-5 border-t border-border p-4">
                {/* conditions */}
                <div className="space-y-3">
                  <h3 className="font-serif text-base font-bold">{dv.dhaftharu.conditions}</h3>
                  {isMetaLoading ? (
                    <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
                  ) : meta ? (
                    <ConditionBuilder meta={meta} value={condConditions} onChange={setCondConditions} />
                  ) : null}
                </div>

                {/* columns */}
                <div className="space-y-3">
                  <h3 className="font-serif text-base font-bold">{dv.dhaftharu.columnsSection}</h3>
                  {isMetaLoading ? (
                    <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
                  ) : meta ? (
                    <ColumnPicker meta={meta} value={condColumns} onChange={setCondColumns} />
                  ) : null}
                </div>

                {/* save */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => saveConditional.mutate()}
                      disabled={saveConditional.isPending || !meta || hasIncompleteConditions}
                      className="btn-primary"
                    >
                      {saveConditional.isPending ? dv.common.saving : dv.common.save}
                    </button>
                    {condSaveError && (
                      <p className="text-sm text-destructive" role="alert">{condSaveError}</p>
                    )}
                  </div>
                  {hasIncompleteConditions && (
                    <p className="text-sm text-[hsl(var(--warning))]" role="alert">
                      {dv.dhaftharu.incompleteConditions}
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── CONDITIONAL: the register (live) ── */}
          <section className="space-y-3">
            <h2 className="section-heading">
              <span className="font-serif text-lg font-bold">{dv.dhaftharu.previewHeading}</span>
              {data.rows.length > 0 && (
                <span className="pill pill-neutral shrink-0 tabular">{data.rows.length}</span>
              )}
            </h2>
            <RegisterPreviewTable columnLabels={data.columnLabels} rows={data.rows} />
          </section>
        </>
      )}
    </div>
  );
}
